import { useState, useCallback, useEffect } from "react";
import { toast } from "sonner";
import { Users2, GitMerge, X, Loader2, Check, ShieldCheck } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { api } from "@/lib/api";

const DISMISS_KEY = "dupDismissed";
const loadDismissed = () => { try { return new Set(JSON.parse(localStorage.getItem(DISMISS_KEY) || "[]")); } catch { return new Set(); } };
const saveDismissed = (set) => localStorage.setItem(DISMISS_KEY, JSON.stringify([...set]));

// A "bare" name has no first name (e.g. "Davis" or "Davis,") — the classic accidental dupe.
const hasFirst = (n) => (n.includes(",") ? (n.split(",")[1] || "").trim().length > 0 : n.trim().split(/\s+/).length > 1);

// Manual scan for duplicate GUESTS (fuzzy name match). Lives in Settings — only runs
// when tapped, so it never slows down normal browsing.
export const DuplicateFinder = ({ onMerged, openSignal = 0 }) => {
  const [open, setOpen] = useState(false);
  const [groups, setGroups] = useState([]);
  const [loading, setLoading] = useState(false);
  const [merging, setMerging] = useState(null);
  const [state, setState] = useState({}); // id -> { primary, checked: Set }
  const [dismissed, setDismissed] = useState(loadDismissed);

  const buildState = useCallback((gs) => {
    const s = {};
    gs.forEach((g) => {
      const primary = g.primary;
      const checked = new Set(
        g.members.filter((m) => m.name !== primary && hasFirst(primary) && !hasFirst(m.name)).map((m) => m.name)
      );
      s[g.id] = { primary, checked };
    });
    return s;
  }, []);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api.duplicateProfiles();
      const visible = d.filter((g) => !loadDismissed().has(g.id));
      setGroups(visible);
      setState(buildState(visible));
    } catch { /* ignore */ }
    finally { setLoading(false); }
  }, [buildState]);

  const setPrimary = (id, name) => setState((prev) => {
    const g = prev[id];
    const checked = new Set([...g.checked].filter((n) => n !== name));
    if (g.primary !== name) checked.add(g.primary); // old primary becomes a merge candidate
    return { ...prev, [id]: { primary: name, checked } };
  });

  const toggle = (id, name) => setState((prev) => {
    const g = prev[id];
    const checked = new Set(g.checked);
    checked.has(name) ? checked.delete(name) : checked.add(name);
    return { ...prev, [id]: { ...g, checked } };
  });

  const dismiss = (id) => {
    const next = new Set(dismissed); next.add(id); setDismissed(next); saveDismissed(next);
    setGroups((gs) => gs.filter((g) => g.id !== id));
  };

  const doMerge = async (g) => {
    const { primary, checked } = state[g.id];
    const names = [...checked];
    if (!names.length) return;
    setMerging(g.id);
    try {
      await api.mergeProfiles(primary, names);
      toast.success(`Merged ${names.length} name${names.length > 1 ? "s" : ""} into "${primary}"`);
      setGroups((gs) => gs.filter((x) => x.id !== g.id));
      onMerged && onMerged();
    } catch {
      toast.error("Merge failed");
    } finally { setMerging(null); }
  };

  // Allow a parent (e.g. after a CSV import) to auto-open + scan.
  useEffect(() => {
    if (openSignal > 0) { setOpen(true); refresh(); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openSignal]);

  return (
    <>
      <button
        onClick={() => { setOpen(true); refresh(); }}
        className="flex w-full items-center justify-between rounded-lg border border-zinc-800 bg-zinc-900 px-4 py-3 text-sm text-zinc-200 transition-colors hover:bg-zinc-800"
        data-testid="duplicate-finder-btn"
      >
        <span className="flex items-center gap-2"><Users2 className="h-4 w-4 text-amber-400" /> Check for duplicate guests</span>
        {loading ? <Loader2 className="h-4 w-4 animate-spin text-zinc-300" /> : <span className="text-xs text-zinc-400">Scan now</span>}
      </button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="grid-cols-[minmax(0,1fr)] max-h-[85vh] w-[calc(100vw-1rem)] !max-w-lg overflow-y-auto border-zinc-800 bg-zinc-950 p-4" data-testid="duplicate-finder-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 font-display tracking-tight">
              <Users2 className="h-5 w-5 text-amber-400" /> Possible duplicate guests
            </DialogTitle>
            <p className="text-sm text-zinc-400">Pick which one to keep, tick the names that are the same person, then merge. Their tours move to the kept profile.</p>
          </DialogHeader>

          {loading ? (
            <div className="flex items-center justify-center gap-2 py-10 text-sm text-zinc-400"><Loader2 className="h-5 w-5 animate-spin" /> Scanning…</div>
          ) : groups.length === 0 ? (
            <div className="flex flex-col items-center gap-2 py-10 text-center text-sm text-zinc-400" data-testid="dup-empty">
              <ShieldCheck className="h-8 w-8 text-emerald-400" />
              Your roster looks clean — no likely duplicates.
            </div>
          ) : (
            <div className="space-y-3">
              {groups.map((g) => {
                const st = state[g.id] || { primary: g.primary, checked: new Set() };
                const nChecked = st.checked.size;
                return (
                  <div key={g.id} className="rounded-xl border border-zinc-800 bg-zinc-900/50 p-3" data-testid={`dup-group-${g.id}`}>
                    <div className="space-y-1.5">
                      {g.members.map((m) => {
                        const isPrimary = st.primary === m.name;
                        const isChecked = st.checked.has(m.name);
                        return (
                          <div key={m.name} className="flex items-center gap-2 rounded-lg border border-zinc-800 bg-zinc-950 px-2.5 py-2">
                            {isPrimary ? (
                              <span className="inline-flex items-center gap-1 rounded-md bg-emerald-500/15 px-2 py-1 text-[10px] font-700 uppercase tracking-wide text-emerald-300">Keep</span>
                            ) : (
                              <button
                                onClick={() => toggle(g.id, m.name)}
                                className={`flex h-5 w-5 shrink-0 items-center justify-center rounded border ${isChecked ? "border-amber-500 bg-amber-500 text-zinc-950" : "border-zinc-600"}`}
                                data-testid={`dup-check-${m.name}`}
                                aria-label={`Merge ${m.name}`}
                              >
                                {isChecked && <Check className="h-3.5 w-3.5" />}
                              </button>
                            )}
                            <div className="min-w-0 flex-1">
                              <p className="truncate text-sm font-500 text-zinc-100">{m.name}</p>
                              <p className="text-[11px] text-zinc-400">{m.tours} tour{m.tours === 1 ? "" : "s"}</p>
                            </div>
                            {!isPrimary && (
                              <button onClick={() => setPrimary(g.id, m.name)} className="shrink-0 rounded-md px-2 py-1 text-[11px] text-zinc-300 hover:bg-white/5 hover:text-emerald-300" data-testid={`dup-keep-${m.name}`}>
                                Keep this
                              </button>
                            )}
                          </div>
                        );
                      })}
                    </div>
                    <div className="mt-2.5 flex items-center justify-between gap-2">
                      <button onClick={() => dismiss(g.id)} className="inline-flex items-center gap-1 rounded-md px-2 py-1.5 text-xs text-zinc-400 hover:text-zinc-300" data-testid={`dup-dismiss-${g.id}`}>
                        <X className="h-3.5 w-3.5" /> Not duplicates
                      </button>
                      <button
                        onClick={() => doMerge(g)}
                        disabled={nChecked === 0 || merging === g.id}
                        className="inline-flex items-center gap-1.5 rounded-md bg-amber-500 px-3 py-1.5 text-xs font-600 text-zinc-950 transition-colors hover:bg-amber-400 disabled:cursor-not-allowed disabled:opacity-40"
                        data-testid={`dup-merge-${g.id}`}
                      >
                        {merging === g.id ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <GitMerge className="h-3.5 w-3.5" />}
                        Merge {nChecked > 0 ? nChecked : ""} → {st.primary}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );
};
